"use client";

import type { ImpromptuPronunciationState } from "@/hooks/useImpromptuAttempt";
import {
  IMPROMPTU_BAND_LABELS,
  IMPROMPTU_COPY,
  IMPROMPTU_PACE_BAND_LABELS,
  IMPROMPTU_PRONUNCIATION_ISSUE_LABELS,
  IMPROMPTU_PRONUNCIATION_UNAVAILABLE_COPY,
} from "@/lib/impromptu/copy";
import { formatTimestamp } from "@/lib/impromptu/format";
import type { ImpromptuReport } from "@/lib/impromptu/types";
import { RotateCcw } from "lucide-react";

/**
 * The finished report.
 *
 * Every claim in here is rendered next to the evidence for it, and a claim with
 * no evidence says so rather than standing alone — invented feedback is worse
 * than missing feedback, because a learner cannot tell the difference and will
 * act on both.
 */

const COPY = IMPROMPTU_COPY.report;

function deliveryMetrics(report: ImpromptuReport) {
  const pace = report.delivery.wordsPerMinute
    ? `${Math.round(report.delivery.wordsPerMinute)} WPM, ${IMPROMPTU_PACE_BAND_LABELS[report.delivery.paceBand]}`
    : report.languageCode === "ja" || report.languageCode === "zh"
      ? COPY.paceNotCompared
      : COPY.paceUnavailable;
  const pauses =
    report.delivery.longPauseCount > 0
      ? COPY.longPauses(report.delivery.longPauseCount)
      : COPY.noLongPauses;
  const fillers = report.delivery.fillerCandidates.reduce((total, item) => total + item.count, 0);
  return { pace, pauses, fillers };
}

/**
 * A quote with the timestamp it came from. The timestamp matters: it is what lets
 * a learner check the claim against what they remember saying, and it uses the
 * same clock they watched while recording.
 */
function EvidenceLine({ evidence }: { evidence: ImpromptuReport["keepDoing"]["evidence"] }) {
  if (!evidence) {
    return <p className="mt-3 text-xs font-semibold text-[#8E8E93]">{COPY.unverified}</p>;
  }
  return (
    <blockquote className="mt-3 border-l-2 border-[#FD954F]/60 pl-3 text-sm leading-6 text-[#AEAEB2]">
      “{evidence.quote}”
      <span className="ml-2 whitespace-nowrap text-xs font-semibold text-[#8E8E93]">
        {formatTimestamp(evidence.startMs)}
      </span>
    </blockquote>
  );
}

function DimensionRow({
  label,
  dimension,
}: {
  label: string;
  dimension: ImpromptuReport["message"]["relevance"];
}) {
  return (
    <div className="grid gap-2 py-4 sm:grid-cols-[7rem_7rem_1fr] sm:items-start">
      <div className="text-sm font-bold text-white">{label}</div>
      <div className="text-xs font-bold uppercase tracking-[0.12em] text-[#FD954F]">
        {dimension.score === null
          ? IMPROMPTU_BAND_LABELS.insufficient
          : `${Math.round(dimension.score)}% · ${IMPROMPTU_BAND_LABELS[dimension.band]}`}
      </div>
      <div>
        <p className="text-sm leading-6 text-[#AEAEB2]">{dimension.feedback}</p>
        <EvidenceLine evidence={dimension.evidence} />
      </div>
    </div>
  );
}

function WordChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-xs font-bold text-[#AEAEB2]">
      {children}
    </span>
  );
}

function HighlightChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-md border border-[#FD954F]/25 bg-[#FD954F]/10 px-3 py-2 text-sm font-semibold text-white">
      {children}
    </span>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <dt className="text-xs font-bold text-[#8E8E93]">{label}</dt>
      <dd className="mt-1 text-sm font-bold text-white">{value}</dd>
    </div>
  );
}

function scoreRank(value: number | null) {
  if (value === null) return IMPROMPTU_BAND_LABELS.insufficient;
  if (value >= 90) return COPY.rankExcellent;
  if (value >= 80) return COPY.rankStrong;
  if (value >= 60) return COPY.rankDeveloping;
  return COPY.rankFocus;
}

function ScoreCard({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="border border-white/10 bg-white/[0.035] p-4">
      <div className="flex items-start justify-between gap-3">
        <dt className="text-sm font-bold text-white">{label}</dt>
        <dd className="text-2xl font-extrabold tabular-nums text-[#FD954F]">
          {value === null ? "N/A" : `${Math.round(value)}%`}
        </dd>
      </div>
      <p className="mt-5 text-xs font-bold uppercase tracking-[0.12em] text-[#8E8E93]">
        {scoreRank(value)}
      </p>
      {value !== null && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
          <div
            className="h-full rounded-full bg-[#FD954F]"
            style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
          />
        </div>
      )}
    </div>
  );
}

type ImpromptuReportViewProps = {
  report: ImpromptuReport;
  pronunciation: ImpromptuPronunciationState;
  onRetry: () => void;
  onNewPrompt: () => void;
};

export function ImpromptuReportView({
  report,
  pronunciation,
  onRetry,
  onNewPrompt,
}: ImpromptuReportViewProps) {
  const metrics = deliveryMetrics(report);

  return (
    <div className="mx-auto w-full max-w-5xl pb-28">
      <div className="border-b border-white/10 pb-8">
        {/* Muted rather than orange: "Your report" is a label for the page, and
            leaving it accented would put three identical orange eyebrows in one
            viewport, next to the two below that actually carry a verdict. */}
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8E8E93]">
          {COPY.eyebrow}
        </p>
        <h1 className="mt-3 max-w-4xl text-3xl font-extrabold leading-[1.08] text-white sm:text-4xl">
          {report.headline}
        </h1>
      </div>

      <section className="border-b border-white/10 py-8">
        <h2 className="text-xl font-bold text-white">{COPY.transcriptHeading}</h2>
        <p className="mt-2 text-sm leading-6 text-[#AEAEB2]">{COPY.transcriptNote}</p>
        <div
          className="mt-5 border border-white/10 bg-white/[0.035] p-5 text-lg leading-8 text-[#F9F9F9] sm:p-6"
          aria-label={COPY.transcriptLabel}
        >
          {report.transcript.words.map((word, index) => (
            <span
              key={`${word.startMs}-${index}`}
              className={word.lowConfidence ? "text-[#8E8E93]" : undefined}
              title={word.lowConfidence ? COPY.lowConfidenceTitle : undefined}
            >
              {word.text}
              {report.languageCode === "ja" || report.languageCode === "zh" ? "" : " "}
            </span>
          ))}
        </div>
      </section>

      <section className="border-b border-white/10 py-8">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <h2 className="text-xl font-bold text-white">{COPY.scorecardHeading}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[#AEAEB2]">{COPY.scorecardBody}</p>
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#8E8E93]">
            {COPY.percentageScale}
          </p>
        </div>
        <dl className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ScoreCard label={COPY.relevanceLabel} value={report.message.relevance.score} />
          <ScoreCard label={COPY.structureLabel} value={report.message.structure.score} />
          <ScoreCard label={COPY.grammarLabel} value={report.language.grammar.score} />
          <ScoreCard label={COPY.vocabularyLabel} value={report.language.vocabulary.score} />
          {pronunciation.status === "available" && (
            <>
              <ScoreCard
                label={COPY.pronunciationScoreLabel}
                value={pronunciation.pronunciationScore}
              />
              <ScoreCard label={COPY.accuracyScoreLabel} value={pronunciation.accuracyScore} />
              <ScoreCard label={COPY.fluencyScoreLabel} value={pronunciation.fluencyScore} />
              {pronunciation.prosodyScore !== null && (
                <ScoreCard label={COPY.prosodyScoreLabel} value={pronunciation.prosodyScore} />
              )}
            </>
          )}
        </dl>
      </section>

      <div className="grid gap-8 py-8 lg:grid-cols-[1fr_1fr]">
        <section>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FD954F]">
            {COPY.keepDoing}
          </p>
          <h2 className="mt-2 text-xl font-bold text-white">{report.keepDoing.title}</h2>
          <p className="mt-2 text-sm leading-6 text-[#AEAEB2]">{report.keepDoing.feedback}</p>
          <EvidenceLine evidence={report.keepDoing.evidence} />
        </section>
        <section>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FD954F]">
            {COPY.tryNext}
          </p>
          <h2 className="mt-2 text-xl font-bold text-white">{report.tryNext.title}</h2>
          <p className="mt-2 text-sm leading-6 text-[#AEAEB2]">{report.tryNext.feedback}</p>
          <EvidenceLine evidence={report.tryNext.evidence} />
        </section>
      </div>

      <section className="border-t border-white/10 py-8">
        <h2 className="text-xl font-bold text-white">{COPY.deliveryHeading}</h2>
        <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          <Metric label={COPY.paceLabel} value={metrics.pace} />
          <Metric label={COPY.pausesLabel} value={metrics.pauses} />
          <Metric label={COPY.fillersLabel} value={metrics.fillers} />
          <Metric
            label={
              report.languageCode === "ja" || report.languageCode === "zh"
                ? COPY.speechUnitsLabel
                : COPY.wordsLabel
            }
            value={report.delivery.wordCount}
          />
        </dl>

        {(report.delivery.fillerCandidates.length > 0 ||
          report.delivery.repeatedWords.length > 0) && (
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <h3 className="text-sm font-bold text-white">{COPY.fillersSubheading}</h3>
              <p className="mt-1 text-xs leading-5 text-[#8E8E93]">{COPY.fillersNote}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {report.delivery.fillerCandidates.map((item) => (
                  <WordChip key={item.word}>
                    {item.word} ×{item.count}
                  </WordChip>
                ))}
                {report.delivery.fillerCandidates.length === 0 && (
                  <span className="text-sm text-[#8E8E93]">{COPY.fillersEmpty}</span>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">{COPY.repetitionSubheading}</h3>
              <p className="mt-1 text-xs leading-5 text-[#8E8E93]">{COPY.repetitionNote}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {report.delivery.repeatedWords.map((item) => (
                  <WordChip key={item.word}>
                    {item.word} ×{item.count}
                  </WordChip>
                ))}
                {report.delivery.repeatedWords.length === 0 && (
                  <span className="text-sm text-[#8E8E93]">{COPY.repetitionEmpty}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="border-t border-white/10 py-8">
        <h2 className="text-xl font-bold text-white">{COPY.messageHeading}</h2>
        <div className="mt-2 divide-y divide-white/10">
          <DimensionRow label={COPY.relevanceLabel} dimension={report.message.relevance} />
          <DimensionRow label={COPY.structureLabel} dimension={report.message.structure} />
        </div>
      </section>

      <section className="border-t border-white/10 py-8">
        <h2 className="text-xl font-bold text-white">{COPY.languageHeading}</h2>
        <div className="mt-2 divide-y divide-white/10">
          <DimensionRow label={COPY.grammarLabel} dimension={report.language.grammar} />
          <DimensionRow label={COPY.vocabularyLabel} dimension={report.language.vocabulary} />
        </div>
        {report.preciseWording.length > 0 && (
          <div className="mt-5">
            <h3 className="text-sm font-bold text-white">{COPY.preciseWordingHeading}</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {report.preciseWording.map((phrase) => (
                <HighlightChip key={phrase}>{phrase}</HighlightChip>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="border-t border-white/10 py-8">
        <h2 className="text-xl font-bold text-white">{COPY.sharperVersionHeading}</h2>
        <p className="mt-4 max-w-3xl text-base leading-7 text-[#F9F9F9]">{report.sharperVersion}</p>
      </section>

      {/* Absent entirely when pronunciation was never requested, rather than
          present and empty: an empty section reads as a missing result. */}
      {pronunciation.status !== "idle" && (
        <section className="border-t border-white/10 py-8" aria-live="polite">
          <h2 className="text-xl font-bold text-white">{COPY.pronunciationHeading}</h2>
          {pronunciation.status === "processing" && (
            <p className="mt-3 text-sm leading-6 text-[#AEAEB2]">{COPY.pronunciationProcessing}</p>
          )}
          {pronunciation.status === "unavailable" && (
            <p className="mt-3 text-sm leading-6 text-[#AEAEB2]">
              {IMPROMPTU_PRONUNCIATION_UNAVAILABLE_COPY[pronunciation.reason]}
            </p>
          )}
          {pronunciation.status === "available" && (
            <div className="mt-5">
              {pronunciation.wordsToReview.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-white">{COPY.wordsToReviewHeading}</h3>
                  <div className="mt-3 divide-y divide-white/10 border-y border-white/10">
                    {pronunciation.wordsToReview.map((word) => (
                      <div key={word.word} className="py-4">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-base font-bold text-white">{word.word}</p>
                          <p className="text-sm font-bold tabular-nums text-[#FD954F]">
                            {Math.round(word.accuracyScore)}%
                          </p>
                        </div>
                        {word.errorType && (
                          <p className="mt-1 text-xs leading-5 text-[#AEAEB2]">
                            {IMPROMPTU_PRONUNCIATION_ISSUE_LABELS[word.errorType]}
                          </p>
                        )}
                        {word.syllables.length > 0 && (
                          <p className="mt-2 text-xs leading-5 text-[#8E8E93]">
                            <span className="font-bold text-[#AEAEB2]">{COPY.syllablesLabel}:</span>{" "}
                            {word.syllables
                              .map(
                                (syllable) =>
                                  `${syllable.text} ${Math.round(syllable.accuracyScore)}%`
                              )
                              .join(" · ")}
                          </p>
                        )}
                        {word.phonemes.length > 0 && (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className="text-xs font-bold text-[#AEAEB2]">
                              {COPY.soundsLabel}
                            </span>
                            {word.phonemes.map((phoneme, index) => (
                              <WordChip key={`${word.word}-${phoneme.text}-${index}`}>
                                /{phoneme.text}/ {Math.round(phoneme.accuracyScore)}%
                              </WordChip>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <div className="sticky bottom-3 mt-4 flex flex-col gap-2 rounded-lg border border-white/10 bg-[#15161B] p-3 shadow-2xl sm:flex-row">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-5 text-sm font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F]"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          {COPY.retryPrompt}
        </button>
        <button
          type="button"
          onClick={onNewPrompt}
          className="min-h-12 rounded-lg border border-white/10 bg-white/[0.04] px-5 text-sm font-bold text-white transition-colors hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F]"
        >
          {COPY.newPrompt}
        </button>
      </div>
    </div>
  );
}
